import React, { useCallback, useEffect, useState } from 'react';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { Box, Button, Typography } from '@mui/material';
import { RETAIL_GOOGLE_CLIENT_ID } from '../../config/retailIdentity';
import { AccountChoiceError, IdentityError, IdentityUnavailableError } from './errors';
import WaitlistForm from '../../features/waitlist/WaitlistForm';
import { emailFromGoogleCredential } from '../../features/waitlist/googleEmail';
import { WAITLIST_COPY } from '../../features/waitlist/waitlistCopy';
import { retailIdentity, retailSession } from './retailSession';

const CHECKING = 'checking';
const SIGNED_IN = 'signedIn';
const SIGNED_OUT = 'signedOut';

/**
 * The identity build's first door: nothing below it renders until the person holds a DWLF Retail
 * identity session (the SPT sign-in stays inside, for account data). A session this browser
 * already holds is carried on; a busy or unreachable identity is a message, never a sign-out.
 *
 * The two sessions are one person's: when the identity session ends, or a refresh comes back as
 * someone else, `onSessionEnded` must end the SPT session too, so whoever signs in next never
 * inherits it.
 */
export default function IdentityGate({ children, onSessionEnded, sessionManager = retailSession, identity = retailIdentity }) {
  const [status, setStatus] = useState(CHECKING);
  const [error, setError] = useState(null);
  const [choice, setChoice] = useState(null);
  // Identity refused this person (403: not invited, or no access to retail). Retail is invite-only,
  // so the refusal is an offer to join the waitlist, with the address Google gave pre-filled.
  const [refused, setRefused] = useState(null);

  const restore = useCallback(async () => {
    setError(null);
    if (!sessionManager.hasStoredSession()) {
      setStatus(SIGNED_OUT);
      return;
    }
    setStatus(CHECKING);
    try {
      setStatus((await sessionManager.refresh()) === null ? SIGNED_OUT : SIGNED_IN);
    } catch (e) {
      setError(e instanceof IdentityUnavailableError ? e.message : 'Could not restore your session.');
      setStatus(SIGNED_OUT);
    }
  }, [sessionManager]);

  // Restoring rotates the refresh token, so it runs once per session manager, never because a
  // callback prop changed.
  useEffect(() => {
    restore();
  }, [restore]);

  useEffect(() => {
    const offEnded = sessionManager.onEnded(() => {
      setStatus(SIGNED_OUT);
      onSessionEnded();
    });
    const offSwitched = sessionManager.onSwitched(() => onSessionEnded());
    return () => {
      offEnded();
      offSwitched();
    };
  }, [sessionManager, onSessionEnded]);

  const signIn = async (credential, accountId) => {
    setError(null);
    try {
      sessionManager.adopt(await identity.signIn(credential, accountId));
      setChoice(null);
      setStatus(SIGNED_IN);
    } catch (e) {
      if (e instanceof AccountChoiceError) {
        setChoice({ credential, accounts: e.accounts });
        return;
      }
      if (e instanceof IdentityError && e.status === 403) {
        setRefused({ email: emailFromGoogleCredential(credential) });
        return;
      }
      setError(e.message || 'Sign-in failed.');
    }
  };

  if (status === SIGNED_IN) {
    return children;
  }
  if (refused) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: 'var(--dwlf-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 3, py: 6 }}>
        <Box sx={{ width: '100%', maxWidth: 480 }}>
          <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontSize: '1.75rem', fontWeight: 500, color: 'var(--dwlf-text)', mb: 1 }}>
            {WAITLIST_COPY.heading}
          </Typography>
          <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.9rem', color: 'var(--dwlf-ink-70)', mb: 3 }}>
            {WAITLIST_COPY.refusedGoogle} {WAITLIST_COPY.intro}
          </Typography>
          <WaitlistForm initialEmail={refused.email} place="refused" />
          <Button variant="text" onClick={() => setRefused(null)} sx={{ mt: 3, px: 0 }}>
            {WAITLIST_COPY.differentAccount}
          </Button>
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'var(--dwlf-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 3 }}>
      <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontSize: '2rem', fontWeight: 600, color: 'var(--dwlf-accent)', mb: 6, letterSpacing: '-0.02em' }}>
        DWLF
      </Typography>
      <Box sx={{ width: '100%', maxWidth: 380 }}>
        <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontSize: '1.75rem', fontWeight: 500, color: 'var(--dwlf-text)', mb: 1 }}>
          Sign in to DWLF Retail
        </Typography>
        <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.85rem', color: 'var(--dwlf-ink-50)', mb: 4 }}>
          {status === CHECKING ? 'Restoring your session…' : 'Preview build: sign in with your invited Google account.'}
        </Typography>
        {error && (
          <Typography role="alert" sx={{ color: 'var(--dwlf-danger, #d32f2f)', mb: 2, fontSize: '0.9rem' }}>
            {error}
          </Typography>
        )}
        {status === SIGNED_OUT && !choice && (
          <GoogleOAuthProvider clientId={RETAIL_GOOGLE_CLIENT_ID}>
            <GoogleLogin onSuccess={(r) => signIn(r.credential)} onError={() => setError('Google sign-in failed.')} />
          </GoogleOAuthProvider>
        )}
        {status === SIGNED_OUT && !choice && (
          <Box component="a" href="/waitlist?from=sign_in" sx={{ display: 'inline-block', mt: 3, fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.85rem', color: 'var(--dwlf-accent)' }}>
            {WAITLIST_COPY.noInviteLink}
          </Box>
        )}
        {choice && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ color: 'var(--dwlf-text)', mb: 1 }}>Choose an account</Typography>
            {choice.accounts.map((a) => (
              <Button key={a.accountId} variant="outlined" onClick={() => signIn(choice.credential, a.accountId)}>
                {a.name}
              </Button>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
